/**
 * @fileoverview Checkbox input component with indeterminate state support
 * @module ctl-checkbox
 * @version 1.0.0
 * 
 * @description
 * A styled checkbox component that wraps a native HTML checkbox input.
 * Provides consistent visual design, support for checked, unchecked, and indeterminate states,
 * and full accessibility support. The component can display optional label text as a slot.
 * 
 * The checkbox supports all standard form behaviors including disabled state, required validation,
 * and form submission with name/value pairs. The indeterminate state is useful for "select all"
 * checkboxes in lists where some but not all items are selected.
 * 
 * @example
 * <!-- Basic checkbox -->
 * <ctl-checkbox>Accept terms</ctl-checkbox>
 * 
 * @example
 * <!-- Checked checkbox with name/value for forms -->
 * <ctl-checkbox name="subscribe" value="yes" checked>
 *   Subscribe to newsletter
 * </ctl-checkbox>
 * 
 * @example
 * <!-- Disabled checkbox -->
 * <ctl-checkbox disabled>Cannot change this</ctl-checkbox>
 * 
 * @example
 * <!-- Indeterminate state (useful for "select all") -->
 * <ctl-checkbox indeterminate>Select all items</ctl-checkbox>
 */

const template = document.createElement("template");

template.innerHTML = `
<style>
  :host {
    --ctl-font: system-ui,-apple-system,Segoe UI,Roboto,Arial;

    display: inline-flex;
    align-items: center;
    gap: var(--ctl-space-2);
    font-family: var(--ctl-font);
    color: var(--ctl-color-text);
    cursor: pointer;
    user-select: none;
  }

  :host([disabled]) {
    opacity: var(--ctl-opacity-disabled);
    cursor: not-allowed;
  }

  .checkbox-wrapper {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  input[type="checkbox"] {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
    margin: 0;
    pointer-events: none;
  }

  .checkbox {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: var(--ctl-control-size);
    height: var(--ctl-control-size);
    border: var(--ctl-border-width-thick) solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-sm);
    background: var(--ctl-color-surface);
    transition: all var(--ctl-transition-normal);
    box-sizing: border-box;
  }

  :host(:not([disabled])) .checkbox-wrapper:hover .checkbox {
    border-color: var(--ctl-color-focus);
  }

  input[type="checkbox"]:focus-visible ~ .checkbox {
    outline: var(--ctl-focus-ring-width) solid var(--ctl-color-focus);
    outline-offset: var(--ctl-focus-ring-offset);
  }

  input[type="checkbox"]:checked ~ .checkbox {
    background: var(--ctl-color-focus);
    border-color: var(--ctl-color-focus);
  }

  input[type="checkbox"]:indeterminate ~ .checkbox {
    background: var(--ctl-color-focus);
    border-color: var(--ctl-color-focus);
  }

  .checkmark {
    display: none;
    width: calc(var(--ctl-control-size) * 0.6);
    height: calc(var(--ctl-control-size) * 0.6);
    color: var(--ctl-color-white);
  }

  input[type="checkbox"]:checked ~ .checkbox .checkmark {
    display: block;
  }

  .indeterminate-mark {
    display: none;
    width: calc(var(--ctl-control-size) * 0.5);
    height: var(--ctl-border-width-thick);
    background: var(--ctl-color-white);
    border-radius: var(--ctl-radius-sm);
  }

  input[type="checkbox"]:indeterminate ~ .checkbox .indeterminate-mark {
    display: block;
  }

  input[type="checkbox"]:indeterminate ~ .checkbox .checkmark {
    display: none;
  }

  :host([disabled]) .checkbox {
    background: color-mix(in srgb, var(--ctl-color-black) 5%, transparent);
    cursor: not-allowed;
  }

  :host([invalid]) .checkbox {
    border-color: var(--ctl-color-danger);
  }

  label {
    cursor: pointer;
    line-height: var(--ctl-leading-normal);
    font-size: var(--ctl-font-size-base);
  }

  :host([disabled]) label {
    cursor: not-allowed;
  }
</style>

<div class="checkbox-wrapper">
  <input
    type="checkbox"
    id="input"
    part="input"
  />
  <span class="checkbox" part="checkbox">
    <svg class="checkmark" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
    <span class="indeterminate-mark"></span>
  </span>
</div>
<label for="input" part="label">
  <slot></slot>
</label>
`;

/**
 * Checkbox Web Component
 * 
 * A styled checkbox input with support for checked, unchecked, and indeterminate states.
 * 
 * @class CtlCheckbox
 * @extends HTMLElement
 * 
 * @attr {boolean} checked - Whether checkbox is checked
 * @attr {boolean} indeterminate - Whether checkbox is in indeterminate state (partially checked)
 * @attr {boolean} disabled - Whether checkbox is disabled
 * @attr {boolean} required - Whether checkbox is required
 * @attr {boolean} invalid - Whether checkbox is in invalid state
 * @attr {string} name - Form field name for submission
 * @attr {string} value - Value submitted when checked (default: "on")
 * @attr {string} aria-label - Accessible label
 * @attr {string} aria-labelledby - ID of labeling element
 * @attr {string} aria-describedby - ID of describing element
 * @attr {boolean} aria-invalid - ARIA invalid state
 * 
 * @slot - Default slot for checkbox label text
 * 
 * @fires ctl-change - Custom event with detail.checked property
 * 
 * @cssprop --ctl-font - Font family (default: system-ui,-apple-system,Segoe UI,Roboto,Arial)
 * @cssprop --ctl-color-text - Text color (default: #111827)
 * @cssprop --ctl-color-muted - Muted text color (default: #6b7280)
 * @cssprop --ctl-color-border - Border color (default: #e5e7eb)
 * @cssprop --ctl-color-surface - Background color (default: #ffffff)
 * @cssprop --ctl-color-focus - Focus ring color (default: #4f46e5)
 * @cssprop --ctl-checkbox-size - Size of the checkbox box (default: 20px)
 */
export class CtlCheckbox extends HTMLElement {
  /**
   * Observed HTML attributes that trigger attributeChangedCallback
   * @returns {string[]} Array of attribute names to observe
   */
  static get observedAttributes() {
    return [
      "checked",
      "indeterminate",
      "disabled",
      "required",
      "invalid",
      "name",
      "value",
      "aria-label",
      "aria-labelledby",
      "aria-describedby",
      "aria-invalid"
    ];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._input = this.shadowRoot.getElementById("input");
    this._onChange = this._onChange.bind(this);
    this._onClick = this._onClick.bind(this);
  }

  connectedCallback() {
    this._upgradeProperty("checked");
    this._upgradeProperty("indeterminate");
    this._upgradeProperty("disabled");
    this._upgradeProperty("value");
    this._upgradeProperty("name");

    this._input.addEventListener("change", this._onChange);
    this.addEventListener("click", this._onClick);
    this._syncToInput();
  }

  disconnectedCallback() {
    this._input.removeEventListener("change", this._onChange);
    this.removeEventListener("click", this._onClick);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    this._syncToInput(name);
  }

  // --- Properties ---
  get checked() {
    return this.hasAttribute("checked");
  }

  set checked(value) {
    if (value) {
      this.setAttribute("checked", "");
      // Clear indeterminate when explicitly setting checked
      this.removeAttribute("indeterminate");
    } else {
      this.removeAttribute("checked");
    }
  }

  get indeterminate() {
    return this.hasAttribute("indeterminate");
  }

  set indeterminate(value) {
    if (value) {
      this.setAttribute("indeterminate", "");
      // Clear checked when setting indeterminate
      this.removeAttribute("checked");
    } else {
      this.removeAttribute("indeterminate");
    }
  }

  get disabled() {
    return this.hasAttribute("disabled");
  }

  set disabled(value) {
    if (value) this.setAttribute("disabled", "");
    else this.removeAttribute("disabled");
  }

  get required() {
    return this.hasAttribute("required");
  }

  set required(value) {
    if (value) this.setAttribute("required", "");
    else this.removeAttribute("required");
  }

  get value() {
    return this.getAttribute("value") || "on";
  }

  set value(val) {
    if (val == null) this.removeAttribute("value");
    else this.setAttribute("value", String(val));
  }

  get name() {
    return this.getAttribute("name");
  }

  set name(val) {
    if (val == null) this.removeAttribute("name");
    else this.setAttribute("name", String(val));
  }

  focus(options) {
    this._input.focus(options);
  }

  blur() {
    this._input.blur();
  }

  // --- Internals ---
  _upgradeProperty(prop) {
    if (Object.prototype.hasOwnProperty.call(this, prop)) {
      const value = this[prop];
      delete this[prop];
      this[prop] = value;
    }
  }

  _syncToInput(changedName) {
    if (!changedName || changedName === "checked") {
      this._input.checked = this.hasAttribute("checked");
    }

    if (!changedName || changedName === "indeterminate") {
      this._input.indeterminate = this.hasAttribute("indeterminate");
    }

    if (!changedName || changedName === "disabled") {
      this._input.disabled = this.hasAttribute("disabled");
    }

    if (!changedName || changedName === "required") {
      this._input.required = this.hasAttribute("required");
    }

    if (!changedName || changedName === "name") {
      const name = this.getAttribute("name");
      if (name) this._input.setAttribute("name", name);
      else this._input.removeAttribute("name");
    }

    if (!changedName || changedName === "value") {
      const value = this.getAttribute("value");
      if (value) this._input.setAttribute("value", value);
      else this._input.removeAttribute("value");
    }

    // ARIA attributes
    const ariaAttrs = ["aria-label", "aria-labelledby", "aria-describedby", "aria-invalid"];
    for (const attr of ariaAttrs) {
      if (!changedName || changedName === attr) {
        if (this.hasAttribute(attr)) {
          this._input.setAttribute(attr, this.getAttribute(attr));
        } else {
          this._input.removeAttribute(attr);
        }
      }
    }

    // Sync aria-invalid based on invalid attribute if not explicitly set
    if (!changedName || changedName === "invalid") {
      if (!this.hasAttribute("aria-invalid")) {
        if (this.hasAttribute("invalid")) {
          this._input.setAttribute("aria-invalid", "true");
        } else {
          this._input.removeAttribute("aria-invalid");
        }
      }
    }
  }

  _onClick(e) {
    // Handle click on host element
    if (this.hasAttribute("disabled")) {
      e.preventDefault();
      return;
    }

    // If click target is not the input itself (e.g., label or host),
    // toggle the checkbox programmatically
    if (e.target !== this._input) {
      e.preventDefault();
      this.toggle();
    }
  }

  _onChange(e) {
    // Sync checked state from native input to component
    if (this._input.checked) {
      this.setAttribute("checked", "");
      this.removeAttribute("indeterminate");
    } else {
      this.removeAttribute("checked");
      this.removeAttribute("indeterminate");
    }

    // Emit custom event
    this._emit("ctl-change", {
      name: this.getAttribute("name") || null,
      value: this.value,
      checked: this._input.checked,
      originalEvent: e
    });
  }

  toggle() {
    if (this.hasAttribute("disabled")) return;

    // Clear indeterminate state on toggle
    if (this.hasAttribute("indeterminate")) {
      this.removeAttribute("indeterminate");
      this.setAttribute("checked", "");
    } else {
      this.checked = !this.checked;
    }

    // Trigger change event
    this._emit("ctl-change", {
      name: this.getAttribute("name") || null,
      value: this.value,
      checked: this.checked,
      originalEvent: null
    });
  }

  _emit(name, detail) {
    this.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        composed: true,
        detail
      })
    );
  }
}

customElements.define("ctl-checkbox", CtlCheckbox);
